/* (c) Dalineage, s.r.o. 2020-2024, all rights reserved */
package com.dalineage.common.adt

import scala.collection.immutable.ListMap
import io.circe.Codec

/*  Inherited attributes. Contains options for processing */
object ProcessingInfoADT {
  import Dialects._

  sealed trait AnalyzerMode derives Codec.AsObject
  case object StrictMode extends AnalyzerMode
  case object AutocreateMode extends AnalyzerMode
  case object UnknownMode extends AnalyzerMode

  case class ProcessingInfo(
    sourceSpec: SourceSpec,
    index: Int,
    startTime: Long,
    passInfo: PassInfo,
  ) derives Codec.AsObject {
    def workDir = passInfo.workDir
  }

  case class PassInfo(
    batchInfo: BatchInfoADT.BatchInfo,
    passNr: Int,
  ) derives Codec.AsObject {
    def strictFirstThenAC = batchInfo.autoCreate && batchInfo.analyzer.strict
    lazy val sourcesSize = batchInfo.sources.size
    def workDir = batchInfo.analyzer.workDir
  }

  case class PowerBISourceSpec(
    parameters: Option[ListMap[String, String]], //only first table in dataset is set
    tableId: Option[Int] = None,
    datasetId: Option[Int] = None,
    tableName: Option[String] = None,
    outputColumns: Option[List[String]] = None,
  ) derives Codec.AsObject

  case class SourceSpec(
    name: String,
    modelPosition: Option[Int],
    defaultDatabase: Option[String],
    defaultSchema: Option[String],
    isExportedDbModel: Boolean,
    isExportedPowerBIModel: Boolean,
    excludeSuffix: List[String],
    ignorePattern: List[String],
    prependHeader: Option[String],
    powerBISourceSpec: Option[PowerBISourceSpec] = None,
    externalId: Option[Int] = None,
    replaceString: Map[String, String] = Map.empty,
  ) derives Codec.AsObject
}

/* Sythetised attributes. Contains results of processing */
object ProcessingStatsADT {

  case class SourceProcessingStats(
    processingInfo: ProcessingInfoADT.ProcessingInfo,
    parserTime: Long,
    analyzerTime: Long,
    graphTime: Long,
    outputTime: Long,
    parsingError: Option[String],
    analyzerError: Option[String],
  ) derives Codec.AsObject

  case class BatchProcessingStats(
    totalFiles: Int,
    relationCount: Int,
    columnCount: Int,
    sourceStats: List[SourceProcessingStats],
    parserErrors : Int = 0,
    analyzerErrors : Int = 0,
    processingEnd: Option[Long] = None,
    analyzerMode: Option[ProcessingInfoADT.AnalyzerMode] = None,
    isModeCompleted: Option[Boolean] = None
  ) derives Codec.AsObject
}
